'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export default function FirstBlog() {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const searchParams = useSearchParams();
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);

  // Listen for changes in the `category` search parameter
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    setCategoryFilter(categoryParam ? parseInt(categoryParam, 10) : null);
  }, [searchParams]);

  const { data: blogs } = useSuspenseQuery({
    queryKey: [
      'blogs',
      categoryFilter,
      { locale, query: { orderBy: 'views', order: 'desc', results: 1 } },
    ],
    queryFn: () =>
      fetchBlogs({
        locale,
        query: {
          orderBy: 'id',
          order: 'desc',
          results: 1,
          where: categoryFilter ? { categories: { in: [categoryFilter] } } : undefined,
        },
      }),
  });

  if (!blogs.data?.length) return null;

  /** TODO: top blogs filter */
  const textContent = getTextFromHTML(blogs.data[0].content).replace(/&nbsp;/gm, '');
  const imageUrl = blogs?.data[0]?.image?.url?.trim() || '/images/placeholder.png';

  return (
    <Link
      href={blogs?.data[0]?.slug ?? '#'}
      prefetch={false}
      className="flex w-full flex-col items-start justify-start gap-4"
    >
      <div className="relative h-[212px] w-full overflow-hidden rounded-[6px] sm:h-[300px] md:h-[385px] lg:h-[520px]">
        <span className="absolute left-4 top-4 z-[10] overflow-hidden rounded-[6px] border border-[#D8D8DB] bg-white px-2 py-1 text-sm font-medium text-primary">
          {blogs?.data[0]?.categories[0]?.name}
        </span>
        <Image
          src={imageUrl}
          alt={resolveImageAlt(blogs?.data?.[0]?.image, blogs?.data?.[0]?.name)}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          sizes={buildImageSizes([{ size: '100vw' }])}
        />
      </div>
      <div className="items-starts flex w-full flex-col justify-start gap-4">
        <h2 data-ea-visual-tag="h3" className="text-lg font-bold text-foreground sm:text-2xl">{blogs.data[0]?.name}</h2>
        <HtmlRendererRaw
          as="p"
          className="line-clamp-4 h-[84px] w-full overflow-hidden text-sm text-[#888888] sm:line-clamp-3 sm:h-[77px] sm:text-base"
          html={textContent}
        />
        <Button className="w-full sm:w-fit" variant={'secondary'}>
          <p>{t('text1148')}</p> <MdOutlineKeyboardArrowRight size={20} />
        </Button>
      </div>
    </Link>
  );
}
